import type {
  CategoryDecisions,
  EffectiveTrackingConsentPolicy,
  ResolvedTrackingConsentStrings,
  TrackingConsentSettingsButtonPosition,
  TrackingConsentSettingsButtonVariant,
  TrackingConsentTheme,
  TrackingConsentThemeFont,
  TrackingConsentTrackerGroup
} from './types';
import { resolveTrackingConsentStrings } from './locales';

const ROOT_CLASS = 'gc-tracking-consent';
const CHOICE_CLASS = `${ROOT_CLASS}__choice`;
const SETTINGS_CLASS = `${ROOT_CLASS}__settings`;
const SVG_NS = 'http://www.w3.org/2000/svg';

type TrackingConsentBannerOptions = {
  policy: EffectiveTrackingConsentPolicy;
  strings?: ResolvedTrackingConsentStrings;
  document: Document;
  id: string;
  initialError: string | null;
  // Read on demand rather than snapshotted: a failed save resets decisions to
  // unknown, and the settings modal must open against that new state.
  getDecisions: () => CategoryDecisions;
  showSettingsButton: boolean;
  settingsButtonPosition: TrackingConsentSettingsButtonPosition;
  settingsButtonVariant: TrackingConsentSettingsButtonVariant;
  onAllowAll: () => void;
  onRejectAll: () => void;
  onSavePreferences: (decisions: Record<string, boolean>) => void;
};

export interface TrackingConsentBannerController {
  openBanner(options?: { focus?: boolean }): void;
  openSettings(decisions: CategoryDecisions): void;
  showError(message: string): void;
  destroy(): void;
}

const createTextElement = (document: Document, tagName: string, className: string, text: string): HTMLElement => {
  const element = document.createElement(tagName);
  element.className = className;
  element.textContent = text;
  return element;
};

/** The cookie body: a disc whose outline doubles back at the top right to carve the bite. */
const COOKIE_BODY_PATH =
  'M12 2a10 10 0 1 0 10 10 1 1 0 0 0-1.31-.95 3 3 0 0 1-3.74-3.74A1 1 0 0 0 16 6a3 3 0 0 1-3-3 1 1 0 0 0-1-1Z';

/** Chips, kept clear of the bitten corner so none of them straddles the edge. */
const COOKIE_CHIP_PATHS = [
  'M7.35 9.4a1.25 1.25 0 1 0 2.5 0 1.25 1.25 0 1 0-2.5 0Z',
  'M14.85 13.6a1.05 1.05 0 1 0 2.1 0 1.05 1.05 0 1 0-2.1 0Z',
  'M10.05 15.6a1.15 1.15 0 1 0 2.3 0 1.15 1.15 0 1 0-2.3 0Z'
];

/**
 * Body and chips as one path, so a host previewing the launcher (the admin
 * editor) renders the exact icon this module mounts. Must be drawn with
 * `fill-rule="evenodd"`, or the chips fill in the body colour and vanish.
 */
export const TRACKING_CONSENT_SETTINGS_ICON_PATH = [COOKIE_BODY_PATH, ...COOKIE_CHIP_PATHS].join(' ');

const createSettingsIcon = (document: Document): SVGSVGElement => {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('class', `${SETTINGS_CLASS}-icon`);
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('width', '24');
  svg.setAttribute('height', '24');
  svg.setAttribute('fill', 'currentColor');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');

  const cookie = document.createElementNS(SVG_NS, 'path');
  cookie.setAttribute('d', TRACKING_CONSENT_SETTINGS_ICON_PATH);
  cookie.setAttribute('fill-rule', 'evenodd');
  svg.append(cookie);

  return svg;
};

const isSafePolicyUrl = (value: string): boolean => {
  try {
    const url = new URL(value);
    return (
      url.protocol === 'https:' ||
      (url.protocol === 'http:' && (url.hostname === 'localhost' || url.hostname === '127.0.0.1'))
    );
  } catch {
    return false;
  }
};

// Mirrors the backend's HEX_COLOR_PATTERN. The banner renders on customer pages
// from a server-supplied theme, so every value is re-checked here rather than
// trusted: a value that fails validation is dropped in favour of the stylesheet
// default instead of being written into a CSS custom property.
const HEX_COLOR_PATTERN = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/iu;

// Conservative subset of CSS <family-name>: letters, digits, spaces, hyphens.
// Anything else (quotes, commas, semicolons, parentheses) is rejected rather
// than escaped, so a family name can never alter the declaration it lands in.
const FONT_FAMILY_PATTERN = /^[a-z0-9 -]{1,100}$/iu;

const isSafeAssetUrl = (value: string): boolean => {
  try {
    return new URL(value).protocol === 'https:';
  } catch {
    return false;
  }
};

/**
 * The themed logo, or null when the theme carries no usable url. Shared by the
 * banner and the settings modal: both are consent surfaces the visitor has to
 * recognise as the organizer's, so neither is left unbranded.
 */
const createLogo = (document: Document, logoUrl: string | null | undefined): HTMLImageElement | null => {
  if (!logoUrl || !isSafeAssetUrl(logoUrl)) {
    return null;
  }
  const logo = document.createElement('img');
  logo.className = `${ROOT_CLASS}__logo`;
  logo.src = logoUrl;
  logo.alt = '';
  logo.setAttribute('role', 'presentation');
  logo.addEventListener('error', () => logo.remove());
  return logo;
};

const loadAssetFont = (document: Document, font: TrackingConsentThemeFont): void => {
  if (font.type !== 'asset' || !font.url || !isSafeAssetUrl(font.url)) {
    return;
  }
  // Asset fonts name a family the device does not have, so the family alone
  // would silently fall back. FontFace avoids injecting a stylesheet.
  const fontSet = (document as Document & { fonts?: { add(font: FontFace): void } }).fonts;
  if (!fontSet || typeof FontFace === 'undefined') {
    return;
  }
  try {
    const face = new FontFace(font.family, `url("${font.url}")`);
    face
      .load()
      .then(loaded => fontSet.add(loaded))
      .catch(() => {
        // A font that will not load is cosmetic: keep the fallback stack.
      });
  } catch {
    // Invalid descriptor; keep the fallback stack.
  }
};

/**
 * Theme colour to the custom property it drives. Every colour the banner renders
 * is themed through this table, so a value left unset falls back to the
 * stylesheet default rather than being derived from another field.
 */
const THEME_COLOR_PROPERTIES: ReadonlyArray<[keyof TrackingConsentTheme, string]> = [
  ['background_color', '--gc-consent-surface'],
  ['text_color', '--gc-consent-text'],
  ['muted_text_color', '--gc-consent-muted'],
  ['link_color', '--gc-consent-link'],
  ['border_color', '--gc-consent-border'],
  ['action_text_color', '--gc-consent-action-text']
];

const applyTheme = (element: HTMLElement, theme: TrackingConsentTheme | null, document: Document): void => {
  const themeFont = theme?.font;
  if (themeFont?.family && FONT_FAMILY_PATTERN.test(themeFont.family)) {
    element.style.setProperty(
      '--gc-consent-font-family',
      `"${themeFont.family}", ui-sans-serif, system-ui, sans-serif`
    );
    loadAssetFont(document, themeFont);
  }
  for (const [field, property] of THEME_COLOR_PROPERTIES) {
    const value = theme?.[field];
    if (typeof value === 'string' && HEX_COLOR_PATTERN.test(value)) {
      element.style.setProperty(property, value);
    }
  }
  // `action_color` and `action_hover_color` are handled outside the table above
  // because each drives two custom properties rather than one.
  if (theme?.action_color && HEX_COLOR_PATTERN.test(theme.action_color)) {
    element.style.setProperty('--gc-consent-action-surface', theme.action_color);
    element.style.setProperty('--gc-consent-action-border', theme.action_color);
  }
  if (theme?.action_hover_color && HEX_COLOR_PATTERN.test(theme.action_hover_color)) {
    element.style.setProperty('--gc-consent-action-hover', theme.action_hover_color);
    element.style.setProperty('--gc-consent-action-wash', 'transparent');
  }
  if (typeof theme?.corner_radius === 'number' && Number.isFinite(theme.corner_radius)) {
    const radius = Math.min(Math.max(Math.trunc(theme.corner_radius), 0), 24);
    element.style.setProperty('--gc-consent-radius', `${radius}px`);
    element.style.setProperty('--gc-consent-control-radius', `${radius}px`);
  }
};

const createCategoryRow = (
  document: Document,
  group: TrackingConsentTrackerGroup,
  strings: ResolvedTrackingConsentStrings,
  pending: Record<string, boolean>,
  onPendingChange: () => void
): HTMLElement => {
  const row = document.createElement('div');
  row.className = `${ROOT_CLASS}__category`;
  row.dataset.category = group.key;
  row.dataset.testid = 'consent-category-row';

  const copy = document.createElement('div');
  const name = createTextElement(document, 'h3', `${ROOT_CLASS}__category-name`, group.name);
  const purpose = createTextElement(document, 'p', `${ROOT_CLASS}__category-purpose`, group.purpose);
  copy.append(name, purpose);

  if (!group.controllable) {
    name.append(createTextElement(document, 'span', `${ROOT_CLASS}__required`, ` (${strings.required_label})`));
    row.append(copy, createTextElement(document, 'span', `${ROOT_CLASS}__always-on`, strings.always_on_label));
    return row;
  }

  const toggle = document.createElement('button');
  toggle.type = 'button';
  toggle.className = `${ROOT_CLASS}__toggle`;
  toggle.setAttribute('role', 'switch');
  toggle.setAttribute('aria-label', group.name);
  toggle.setAttribute('aria-checked', String(pending[group.key]));
  toggle.addEventListener('click', () => {
    pending[group.key] = !pending[group.key];
    toggle.setAttribute('aria-checked', String(pending[group.key]));
    onPendingChange();
  });
  row.append(copy, toggle);
  return row;
};

export const createTrackingConsentBanner = ({
  policy,
  strings: suppliedStrings,
  document,
  id,
  initialError,
  getDecisions,
  showSettingsButton,
  settingsButtonPosition,
  settingsButtonVariant,
  onAllowAll,
  onRejectAll,
  onSavePreferences
}: TrackingConsentBannerOptions): TrackingConsentBannerController => {
  const strings = suppliedStrings ?? resolveTrackingConsentStrings(undefined, policy.banner, policy.tracker_groups);
  let bannerElement: HTMLElement | null = null;
  let errorElement: HTMLElement | null = null;
  let settingsButton: HTMLButtonElement | null = null;
  let modalBackdrop: HTMLElement | null = null;
  let modalElement: HTMLElement | null = null;
  let modalErrorElement: HTMLElement | null = null;
  // Whether the banner was on screen when settings opened, so closing settings
  // can put an undecided visitor back where they were instead of leaving them
  // with no way to consent.
  let bannerWasOpenBeforeSettings = false;
  let previouslyFocusedElement: HTMLElement | null = null;
  let pending: Record<string, boolean> = {};
  let masterToggle: HTMLButtonElement | null = null;
  let keydownHandler: ((event: KeyboardEvent) => void) | null = null;

  const controllableGroups = policy.tracker_groups.filter(group => group.controllable);

  /**
   * The persistent settings button shares a corner with the banner and the
   * modal, and all three carry the same z-index, so it ends up underneath
   * whichever is on screen: its label is then unhoverable and unclickable while
   * the sliver of it that escapes the dialog still reacts. Both surfaces offer
   * their own settings action, so it steps aside while either is open.
   * Inline display beats the `--icon` variant's own `display`, which `hidden`
   * would not.
   */
  const syncSettingsButtonVisibility = (): void => {
    if (!settingsButton) {
      return;
    }
    settingsButton.style.display = bannerElement || modalBackdrop ? 'none' : '';
  };

  const showError = (message: string): void => {
    // A save can fail from either surface. When the modal is open the error
    // belongs inside it: mounting the banner would stack a second dialog behind
    // the modal and move focus outside its aria-modal subtree.
    if (modalElement) {
      if (!modalErrorElement?.isConnected) {
        modalErrorElement = createTextElement(document, 'p', `${ROOT_CLASS}__error`, message);
        modalErrorElement.setAttribute('role', 'alert');
        modalElement.append(modalErrorElement);
      } else {
        modalErrorElement.textContent = message;
      }
      return;
    }

    if (!bannerElement) {
      openBanner({ focus: true });
    }

    if (!errorElement && bannerElement) {
      errorElement = createTextElement(document, 'p', `${ROOT_CLASS}__error`, message);
      errorElement.setAttribute('role', 'alert');
      bannerElement.append(errorElement);
      return;
    }

    if (errorElement) {
      errorElement.textContent = message;
    }
  };

  const createChoiceButton = (
    label: string,
    handler: () => void,
    // Exactly one action per surface is the recommended one, so it carries the
    // themed action colour while the rest step back to an outline.
    variant: 'primary' | 'secondary',
    dataChoice?: string
  ): HTMLButtonElement => {
    const button = createTextElement(
      document,
      'button',
      `${CHOICE_CLASS} ${CHOICE_CLASS}--${variant}`,
      label
    ) as HTMLButtonElement;
    button.type = 'button';
    if (dataChoice) {
      button.dataset.consentChoice = dataChoice;
    }
    button.addEventListener('click', handler);
    return button;
  };

  const openBanner = ({ focus = false }: { focus?: boolean } = {}): void => {
    if (bannerElement) {
      if (focus) {
        bannerElement.querySelector<HTMLButtonElement>('[data-consent-choice="allowed"]')?.focus();
      }
      return;
    }

    const titleId = `${id}-title`;
    const descriptionId = `${id}-description`;
    bannerElement = document.createElement('section');
    bannerElement.className = `${ROOT_CLASS}__dialog`;
    bannerElement.setAttribute('role', 'dialog');
    bannerElement.setAttribute('aria-labelledby', titleId);
    bannerElement.setAttribute('aria-describedby', descriptionId);
    bannerElement.setAttribute('aria-modal', 'false');

    applyTheme(bannerElement, policy.theme, document);

    const copy = document.createElement('div');
    copy.className = `${ROOT_CLASS}__copy`;
    const title = createTextElement(document, 'h2', `${ROOT_CLASS}__title`, strings.title);
    title.id = titleId;
    const description = createTextElement(document, 'p', `${ROOT_CLASS}__description`, strings.description);
    description.id = descriptionId;
    copy.append(title, description);

    const logo = createLogo(document, policy.theme?.logo_url);
    if (logo) {
      copy.prepend(logo);
    }

    if (policy.banner.policy_url && isSafePolicyUrl(policy.banner.policy_url)) {
      const policyLink = createTextElement(
        document,
        'a',
        `${ROOT_CLASS}__policy-link`,
        strings.policy_link_label
      ) as HTMLAnchorElement;
      policyLink.href = policy.banner.policy_url;
      policyLink.target = '_blank';
      policyLink.rel = 'noopener noreferrer';
      copy.append(policyLink);
    }

    const actions = document.createElement('div');
    actions.className = `${ROOT_CLASS}__actions`;
    const allowButton = createChoiceButton(strings.allow_label, onAllowAll, 'primary', 'allowed');
    const rejectButton = createChoiceButton(strings.reject_label, onRejectAll, 'secondary', 'rejected');
    const settingsButtonInBanner = createChoiceButton(
      strings.settings_label,
      () => {
        openSettings(getDecisions());
      },
      'secondary'
    );
    actions.append(allowButton, rejectButton, settingsButtonInBanner);
    bannerElement.append(copy, actions);
    document.body.append(bannerElement);
    syncSettingsButtonVisibility();

    if (initialError) {
      showError(initialError);
    }

    // Only move focus when the banner appears as the result of a visitor action.
    // On first mount it must not interrupt whatever the visitor is already doing.
    if (focus) {
      allowButton.focus();
    }
  };

  const syncMasterToggle = (): void => {
    if (masterToggle) {
      const allOn = controllableGroups.every(g => pending[g.key]);
      masterToggle.setAttribute('aria-checked', String(allOn));
    }
  };

  const getModalFocusables = (): HTMLElement[] => {
    if (!modalBackdrop) {
      return [];
    }
    return Array.from(
      modalBackdrop.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
    ).filter(el => !el.hasAttribute('disabled'));
  };

  const closeSettings = (): void => {
    if (keydownHandler) {
      document.removeEventListener('keydown', keydownHandler);
      keydownHandler = null;
    }
    modalBackdrop?.remove();
    modalBackdrop = null;
    modalElement = null;
    modalErrorElement = null;
    masterToggle = null;
    pending = {};
    syncSettingsButtonVisibility();

    // Settings replaced the banner on open, so restore it. Without this an
    // undecided visitor who opens and closes settings is left with no prompt.
    if (bannerWasOpenBeforeSettings) {
      bannerWasOpenBeforeSettings = false;
      previouslyFocusedElement = null;
      openBanner({ focus: true });
      return;
    }

    if (previouslyFocusedElement?.isConnected) {
      previouslyFocusedElement.focus();
    }
    previouslyFocusedElement = null;
  };

  const openSettings = (decisions: CategoryDecisions): void => {
    if (modalBackdrop) {
      closeSettings();
    }

    // Remove the banner when opening settings
    bannerWasOpenBeforeSettings = !!bannerElement;
    bannerElement?.remove();
    bannerElement = null;
    errorElement = null;

    previouslyFocusedElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    // Initialize pending values from current decisions
    pending = Object.fromEntries(controllableGroups.map(group => [group.key, decisions[group.key] === 'allowed']));

    const titleId = `${id}-settings-title`;
    const descriptionId = `${id}-settings-description`;

    modalBackdrop = document.createElement('div');
    modalBackdrop.className = `${ROOT_CLASS}__backdrop`;
    modalBackdrop.addEventListener('click', event => {
      if (event.target === modalBackdrop) {
        closeSettings();
      }
    });

    const modal = document.createElement('div');
    modalElement = modal;
    modal.className = `${ROOT_CLASS}__modal`;
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-labelledby', titleId);
    modal.setAttribute('aria-describedby', descriptionId);

    applyTheme(modal, policy.theme, document);

    // Close button
    const closeButton = document.createElement('button');
    closeButton.type = 'button';
    closeButton.className = `${ROOT_CLASS}__close`;
    closeButton.setAttribute('aria-label', strings.close_label);
    closeButton.textContent = '✕';
    closeButton.addEventListener('click', closeSettings);

    const modalHeader = document.createElement('div');
    modalHeader.className = `${ROOT_CLASS}__modal-header`;
    const modalTitle = createTextElement(document, 'h2', `${ROOT_CLASS}__title`, strings.title);
    modalTitle.id = titleId;
    const modalDescription = createTextElement(document, 'p', `${ROOT_CLASS}__description`, strings.description);
    modalDescription.id = descriptionId;
    modalHeader.append(modalTitle, modalDescription);
    const modalLogo = createLogo(document, policy.theme?.logo_url);
    if (modalLogo) {
      modalHeader.prepend(modalLogo);
    }
    modalHeader.append(closeButton);

    // Categories section
    const categoriesSection = document.createElement('div');
    categoriesSection.className = `${ROOT_CLASS}__categories`;

    // Header row with master toggle
    const categoriesHeader = document.createElement('div');
    categoriesHeader.className = `${ROOT_CLASS}__categories-header`;
    const categoriesLabel = createTextElement(
      document,
      'span',
      `${ROOT_CLASS}__categories-label`,
      strings.categories_label
    );
    masterToggle = document.createElement('button');
    masterToggle.type = 'button';
    masterToggle.className = `${ROOT_CLASS}__toggle`;
    masterToggle.setAttribute('role', 'switch');
    masterToggle.setAttribute('aria-label', strings.allow_all_optional_categories_label);
    masterToggle.addEventListener('click', () => {
      const allOn = controllableGroups.every(g => pending[g.key]);
      for (const group of controllableGroups) {
        pending[group.key] = !allOn;
      }
      // Update individual toggle displays
      for (const toggle of Array.from(
        modal.querySelectorAll<HTMLButtonElement>(`.${ROOT_CLASS}__toggle[role="switch"]`)
      )) {
        if (toggle !== masterToggle) {
          const categoryKey = toggle.closest(`.${ROOT_CLASS}__category`)?.getAttribute('data-category');
          if (categoryKey && categoryKey in pending) {
            toggle.setAttribute('aria-checked', String(pending[categoryKey]));
          }
        }
      }
      syncMasterToggle();
    });
    categoriesHeader.append(categoriesLabel, masterToggle);
    categoriesSection.append(categoriesHeader);

    // Category rows
    for (const group of strings.tracker_groups) {
      const row = createCategoryRow(document, group, strings, pending, syncMasterToggle);
      categoriesSection.append(row);
    }

    // Footer actions
    const modalActions = document.createElement('div');
    modalActions.className = `${ROOT_CLASS}__modal-actions`;
    const rejectButton = createChoiceButton(
      strings.reject_label,
      () => {
        onRejectAll();
      },
      'secondary'
    );
    const allowButton = createChoiceButton(
      strings.allow_label,
      () => {
        onAllowAll();
      },
      'secondary'
    );
    // The visitor came here to choose per category, so saving that choice is the
    // recommended action rather than either blanket answer.
    const saveButton = createChoiceButton(
      strings.save_preferences_label,
      () => {
        onSavePreferences({ ...pending });
      },
      'primary'
    );
    modalActions.append(rejectButton, allowButton, saveButton);

    modal.append(modalHeader, categoriesSection, modalActions);
    modalBackdrop.append(modal);
    document.body.append(modalBackdrop);
    syncSettingsButtonVisibility();

    syncMasterToggle();

    // Focus trap
    keydownHandler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeSettings();
        return;
      }
      if (event.key === 'Tab') {
        const focusables = getModalFocusables();
        if (focusables.length === 0) {
          return;
        }
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        // Focus can sit outside the modal: the visitor may have clicked the page
        // behind the backdrop, or the banner may have been focused before this
        // opened. Wrapping only at the edges would let Tab walk the host page,
        // so pull focus back in whenever it is not already inside.
        const activeElement = document.activeElement;
        if (!(activeElement instanceof HTMLElement) || !modalBackdrop?.contains(activeElement)) {
          event.preventDefault();
          (event.shiftKey ? last : first).focus();
          return;
        }
        if (event.shiftKey) {
          if (document.activeElement === first) {
            event.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
        }
      }
    };
    document.addEventListener('keydown', keydownHandler);

    closeButton.focus();
  };

  const destroy = (): void => {
    if (keydownHandler) {
      document.removeEventListener('keydown', keydownHandler);
      keydownHandler = null;
    }
    bannerElement?.remove();
    bannerElement = null;
    errorElement = null;

    modalBackdrop?.remove();
    modalBackdrop = null;
    modalElement = null;
    modalErrorElement = null;
    bannerWasOpenBeforeSettings = false;
    masterToggle = null;
    pending = {};

    settingsButton?.remove();
    settingsButton = null;

    if (previouslyFocusedElement?.isConnected) {
      previouslyFocusedElement.focus();
    }
    previouslyFocusedElement = null;
  };

  if (showSettingsButton) {
    const isIcon = settingsButtonVariant === 'icon';
    const className = `${SETTINGS_CLASS} ${SETTINGS_CLASS}--${settingsButtonPosition}${
      isIcon ? ` ${SETTINGS_CLASS}--icon` : ''
    }`;

    if (isIcon) {
      settingsButton = document.createElement('button');
      settingsButton.className = className;
      settingsButton.setAttribute('aria-label', strings.settings_label);
      settingsButton.title = strings.settings_label;
      settingsButton.append(createSettingsIcon(document));
    } else {
      settingsButton = createTextElement(document, 'button', className, strings.settings_label) as HTMLButtonElement;
    }

    settingsButton.type = 'button';
    settingsButton.dataset.consentSettings = '';
    settingsButton.addEventListener('click', () => openSettings(getDecisions()));
    applyTheme(settingsButton, policy.theme, document);
    document.body.append(settingsButton);
  }

  return { openBanner, openSettings, showError, destroy };
};
